import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
	updateExamReqClassID,
	getCompleteGradingRequest,
	deleteExamRecords,
} from "../../components/Api/ExamGradingEndpoints";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import ExamViewSideBar from "./components/ExamViewSideBar";
import {
	CircularProgress,
	Tooltip,
	Divider,
	Chip,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExamViewSnackbar from "./components/ExamViewSnackbar";
import ReconciliationDialog from "./components/ReconciliationDialog";
import FileValidationDialog from "./components/FileValidationDialog";
import KeysAndStatistics from "./components/KeysAndStatistics";
import VersionManagementDialog from "./components/VersionManagementDialog";
import { parseFile } from "./utils/parseRawExamFile";
import ClassAndScores from "./components/ClassAndScores";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { RESET } from "jotai/utils";
import {
	currentOmrRequestAtom,
	examProcessingStateAtom,
	allStudentsMatchedAtom,
	examInDatabaseAtom,
	outputSettingsAtom,
	OUTPUT_SETTINGS_DEFAULT,
	omrRequestsAtom,
	gradeWithoutMatchingAtom,
	termsAtom,
	classListFileAtom,
} from "../../components/Atoms/atoms";
import {
	buildKeyMapByVersion,
	formatExcelTextCell,
	getUniqueWeightBreakdownValues,
	isWeightedExam,
	gradeResponseString,
	toCsv,
} from "./utils/scoring";
import DragAndDropFile from "./components/DragAndDropFile";
import { buildDoc } from "../RFS/utils/pdfReceipt";
import { useTermsQueryFull } from "../../Hooks/useTermsQuery";
import { getClasslistLDap } from "../../components/Api/ExamGradingEndpoints";
import {
	buildStudentSectionLookup,
	normalizeStudentIdentifier,
	parseRootClassIds,
} from "./utils/classSections";
import {
	normalizeVersionList as normalizeVersionListForScoring,
	resolveStudentVersion as resolveStudentVersionForScoring,
} from "./utils/versionResolution";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import LegendToggleIcon from "@mui/icons-material/LegendToggle";
import JoinLeftIcon from "@mui/icons-material/JoinLeft";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";

const AUTO_CLOSE_SIDEBAR_STORAGE_KEY = "exam_sidebar_keep_open";
const SIDEBAR_LAYOUT_STORAGE_KEY = "exam_sidebar_layout_v1";
const SIDEBAR_MIN_WIDTH = 320;
const SIDEBAR_MAX_WIDTH = 720;
const SIDEBAR_COLLAPSED_WIDTH = 64;

function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function getMaxSidebarWidth(viewportWidth) {
	return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, viewportWidth - 32));
}

export default function ExamGrading() {
	const DELETE_MODE = {
		KEYS: "keys",
		STUDENTS: "students",
		BOTH: "both",
	};

	// stable auth
	const auth = useMemo(() => isAuthenticated(), []);
	const token = auth.token;
	const ccid = auth.ccid;
	const access = auth.access;

	const isAdmin = access.includes("ADMIN");

	// atoms
	const setOmrRequests = useSetAtom(omrRequestsAtom);
	const [currentOmrRequest, setCurrentOmrRequest] = useAtom(currentOmrRequestAtom);
	const [examProcessingState, setExamProcessingState] = useAtom(examProcessingStateAtom);
	const [examInDatabase, setExamInDatabase] = useAtom(examInDatabaseAtom);
	const setAllStudentsMatched = useSetAtom(allStudentsMatchedAtom);

	// locals
	const [file, setFile] = useState(null);
	const [reqnumFromResponse, setReqnumFromResponse] = useState(null);
	const [openReconciliationDialog, setOpenReconciliationDialog] = useState(false);
	const [reconciliationState, setReconciliationState] = useState([]);
	const [classIDInputValue, setClassIDInputValue] = useState("");
	const [classIdError, setClassIdError] = useState("");
	const gradeWithoutMatching = useAtomValue(gradeWithoutMatchingAtom);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [keepSidebarOpen, setKeepSidebarOpen] = useState(() => {
		try {
			const storedValue = sessionStorage.getItem(AUTO_CLOSE_SIDEBAR_STORAGE_KEY);
			if (storedValue === null) return true;
			return storedValue === "true";
		} catch {
			return true;
		}
	});
	const [sidebarLayout, setSidebarLayout] = useState(() => {
		try {
			const parsed = JSON.parse(sessionStorage.getItem(SIDEBAR_LAYOUT_STORAGE_KEY) || "{}");
			return {
				mode: ["docked", "slide", "dialog"].includes(parsed?.mode) ? parsed.mode : "docked",
				width: clamp(Number(parsed?.width) || 420, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH),
				offsetX: Number.isFinite(parsed?.offsetX) ? parsed.offsetX : 0,
			};
		} catch {
			return {
				mode: "docked",
				width: 420,
				offsetX: 0,
			};
		}
	});
	const [viewportWidth, setViewportWidth] = useState(() =>
		typeof window === "undefined" ? 1440 : window.innerWidth,
	);
	const sidebarInteractionRef = useRef(null);

	const [outputSettings, setOutputSettings] = useAtom(outputSettingsAtom);
	const inputSettings = {
		multipleFiles: false,
	};

	const [alignment, setAlignment] = useState("exam-info");
	const [editMode, setEditMode] = useState(false);
	const [examFileUploading, setExamFileUploading] = useState(false);
	const [copyClicked, setCopyClicked] = useState(false);
	const [pulse, setPulse] = useState(false);
	const [modifyingClassID, setModifyingClassID] = useState(false);
	const [loadingUpdateClassID, setLoadingUpdateClassID] = useState(false);
	const [classId, setClassId] = useState("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [loadingDelete, setLoadingDelete] = useState(false);
	const [openVersionManagementDialog, setOpenVersionManagementDialog] = useState(false);

	const [rescoreLoading, setRescoreLoading] = useState(false);

	// Re-upload confirmation dialog state
	const [openReuploadConfirmDialog, setOpenReuploadConfirmDialog] = useState(false);
	const [pendingReuploadData, setPendingReuploadData] = useState(null);
	const reuploadDeleting = false;
	const [selectedUploadMode, setSelectedUploadMode] = useState("upsert");

	// File validation dialog state
	const [openFileValidationDialog, setOpenFileValidationDialog] = useState(false);
	const [parsedFileData, setParsedFileData] = useState(null);
	const [originalFileContent, setOriginalFileContent] = useState(null);

	const [classListFile, setClassListFile] = useAtom(classListFileAtom);

	const { data: termsList } = useTermsQueryFull(token);

	const [terms, setTerms] = useAtom(termsAtom);

	const [classListError, setClassListError] = useState({
		error: false,
		term: null,
		classid: null,
		message: "",
	});
	const activeReqnumRef = useRef("");
	const activeClassIdsRef = useRef("");
	const examInfoSectionRef = useRef(null);
	const keysAndStatsSectionRef = useRef(null);
	const classAndScoresSectionRef = useRef(null);
	const emptyClassListFile = useMemo(() => ({ classlist: [], class_ids: "" }), []);
	const defaultClassListError = useMemo(
		() => ({
			error: false,
			term: null,
			classid: null,
			message: "",
		}),
		[],
	);
	const [openDownloadDialog, setOpenDownloadDialog] = useState(false);

	const getClassFileEarly = useCallback(async () => {
		const requestedReqnum = String(currentOmrRequest?.reqnum || "").trim();
		const requestedClassIds = String(currentOmrRequest?.class_ids || "").trim();
		if (!requestedClassIds) return;

		try {
			let termCode = 1940; // fallback
			if (currentOmrRequest?.term) {
				termCode = currentOmrRequest.term; // use term from request if available
			} else if (terms?.termNumber) {
				termCode = terms.termNumber; // use your model’s numeric code 1940; //
			}

			const response = await getClasslistLDap(token, currentOmrRequest.class_ids, String(termCode));
			if (
				activeReqnumRef.current !== requestedReqnum ||
				activeClassIdsRef.current !== requestedClassIds
			) {
				return null;
			}

			setClassListFile({
				classlist: response.ldap_data || [],
				class_ids: currentOmrRequest.class_ids,
			});
			setClassListError({
				error: false,
				term: terms?.termNumber,
				classid: currentOmrRequest.class_ids,
				message: `Successfully fetched class list file for Class ID(s): ${currentOmrRequest.class_ids}  ${terms?.termNumber ? `, Term ${terms?.termNumber}.` : ". Error with Term."}`,
			});

			return response;
		} catch (err) {
			if (
				activeReqnumRef.current !== requestedReqnum ||
				activeClassIdsRef.current !== requestedClassIds
			) {
				return null;
			}
			console.error("Error fetching class list file:", err);
			setClassListError({
				error: true,
				term: terms?.termNumber ? terms?.termNumber : "Error with Term.",
				classid: currentOmrRequest.class_ids,
				message: `Failed to fetch class list file for Class ID(s): ${currentOmrRequest.class_ids}  ${terms?.termNumber ? `, Term ${terms?.termNumber}.` : ". Error with Term."}`,
			});
		}
	}, [
		currentOmrRequest?.reqnum,
		currentOmrRequest?.class_ids,
		currentOmrRequest?.term,
		setClassListFile,
		token,
		terms?.termNumber,
	]);

	useEffect(() => {
		try {
			sessionStorage.setItem(AUTO_CLOSE_SIDEBAR_STORAGE_KEY, String(keepSidebarOpen));
		} catch {
			// no-op when sessionStorage is unavailable
		}
	}, [keepSidebarOpen]);

	useEffect(() => {
		try {
			sessionStorage.setItem(SIDEBAR_LAYOUT_STORAGE_KEY, JSON.stringify(sidebarLayout));
		} catch {
			// no-op when sessionStorage is unavailable
		}
	}, [sidebarLayout]);

	useEffect(() => {
		const handleResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const minimumOffset = Math.min(0, SIDEBAR_MIN_WIDTH - viewportWidth + 48);
		setSidebarLayout((prev) => ({
			...prev,
			offsetX: prev.mode === "docked" ? 0 : clamp(prev.offsetX, minimumOffset, 0),
		}));
	}, [viewportWidth]);

	useEffect(() => {
		activeReqnumRef.current = String(currentOmrRequest?.reqnum || "").trim();
		activeClassIdsRef.current = String(currentOmrRequest?.class_ids || "").trim();
	}, [currentOmrRequest?.reqnum, currentOmrRequest?.class_ids]);

	useEffect(() => {
		// Hard reset request-scoped state as soon as selection changes to prevent cross-request bleed.
		setExamProcessingState(null);
		setExamInDatabase(false);
		setAllStudentsMatched(false);
		setClassListFile({ ...emptyClassListFile });
		setClassListError({ ...defaultClassListError });
		setClassId(String(activeClassIdsRef.current || "").trim());
		setClassIDInputValue("");
		setClassIdError("");
		setModifyingClassID(false);
		setAlignment("exam-info");
		setEditMode(false);
		setFile(null);
		setParsedFileData(null);
		setOriginalFileContent(null);
		setReqnumFromResponse(null);
		setReconciliationState([]);
		setOpenReconciliationDialog(false);
		setSelectedUploadMode("upsert");
		setPendingReuploadData(null);
		setOpenReuploadConfirmDialog(false);
	}, [
		currentOmrRequest?.reqnum,
		setExamProcessingState,
		setExamInDatabase,
		setAllStudentsMatched,
		setClassListFile,
		emptyClassListFile,
		defaultClassListError,
	]);

	useEffect(() => {
		if (currentOmrRequest?.class_ids && termsList) getClassFileEarly();
		else {
			setClassListFile({ ...emptyClassListFile });
			setClassListError({ ...defaultClassListError });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOmrRequest?.class_ids, termsList]);

	useEffect(() => {
		if (termsList && termsList.length > 0) {
			const termDescriptions = termsList.map((term) => term.desc);
			const termNumbers = termsList.map((term) => term.term);
			const termsListVals = termNumbers.map((term, index) => {
				return termDescriptions[index] + " (" + term + ")";
			});

			const highestTermNumber = Math.max(...termNumbers);
			const highestTermIndex = termNumbers.indexOf(highestTermNumber);
			const highestTerm = termsListVals[highestTermIndex];
			setTerms({
				terms: termsListVals,
				termValue: highestTerm,
				termNumber: Number(highestTermNumber),
				termsList: termsList,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [termsList]);

	useEffect(() => {
		setClassId(String(currentOmrRequest?.class_ids || "").trim());
	}, [currentOmrRequest?.class_ids, currentOmrRequest?.reqnum]);

	useEffect(() => {
		setOutputSettings({ ...OUTPUT_SETTINGS_DEFAULT });
	}, [currentOmrRequest?.reqnum, setOutputSettings]);

	const sidebarMode = sidebarLayout.mode;
	const sidebarWidth = sidebarLayout.width;
	const sidebarOffsetX = sidebarLayout.mode === "docked" ? 0 : sidebarLayout.offsetX;
	const isFloatingSidebar = sidebarMode !== "docked";
	const effectiveSidebarWidth = sidebarOpen ? sidebarWidth : SIDEBAR_COLLAPSED_WIDTH;

	const updateSidebarMode = useCallback((mode) => {
		setSidebarLayout((prev) => ({
			...prev,
			mode,
			offsetX: mode === "docked" ? 0 : prev.offsetX,
		}));
		if (mode !== "dialog") {
			setSidebarOpen(true);
		}
	}, []);

	const handleSidebarResizeStart = useCallback(
		(event) => {
			if (event.button !== 0) return;
			event.preventDefault();
			event.stopPropagation();
			sidebarInteractionRef.current = {
				type: "resize",
				startX: event.clientX,
				startWidth: sidebarWidth,
				startOffsetX: sidebarOffsetX,
				mode: sidebarMode,
			};
		},
		[sidebarMode, sidebarOffsetX, sidebarWidth],
	);

	const handleSidebarDragStart = useCallback(
		(event) => {
			if (event.button !== 0 || !isFloatingSidebar) return;
			event.preventDefault();
			event.stopPropagation();
			sidebarInteractionRef.current = {
				type: "drag",
				startX: event.clientX,
				startWidth: sidebarWidth,
				startOffsetX: sidebarOffsetX,
				mode: sidebarMode,
			};
		},
		[isFloatingSidebar, sidebarMode, sidebarOffsetX, sidebarWidth],
	);

	const handleResetSidebarPosition = useCallback(() => {
		setSidebarLayout((prev) => ({ ...prev, offsetX: 0 }));
	}, []);

	useEffect(() => {
		const handlePointerMove = (event) => {
			const interaction = sidebarInteractionRef.current;
			if (!interaction) return;

			if (interaction.type === "resize") {
				const nextWidth = clamp(
					interaction.startWidth - (event.clientX - interaction.startX),
					SIDEBAR_MIN_WIDTH,
					getMaxSidebarWidth(viewportWidth),
				);
				setSidebarLayout((prev) => ({ ...prev, width: nextWidth }));
				return;
			}

			if (interaction.type === "drag" && interaction.mode !== "docked") {
				const minimumOffset = Math.min(0, interaction.startWidth - viewportWidth + 48);
				const nextOffset = clamp(
					interaction.startOffsetX + (event.clientX - interaction.startX),
					minimumOffset,
					0,
				);
				setSidebarLayout((prev) => ({ ...prev, offsetX: nextOffset }));
			}
		};

		const handlePointerUp = () => {
			sidebarInteractionRef.current = null;
		};

		window.addEventListener("mousemove", handlePointerMove);
		window.addEventListener("mouseup", handlePointerUp);

		return () => {
			window.removeEventListener("mousemove", handlePointerMove);
			window.removeEventListener("mouseup", handlePointerUp);
		};
	}, [viewportWidth]);

	const normalizeVersionCode = useCallback((value, fallback = "1") => {
		if (value === null || value === undefined) return fallback;
		const v = String(value).trim();
		return v || fallback;
	}, []);

	const compareVersionCodes = useCallback(
		(a, b) => {
			const av = normalizeVersionCode(a, "1");
			const bv = normalizeVersionCode(b, "1");
			const an = Number.parseInt(av, 10);
			const bn = Number.parseInt(bv, 10);
			const aNumeric = Number.isInteger(an) && String(an) === av;
			const bNumeric = Number.isInteger(bn) && String(bn) === bv;
			if (aNumeric && bNumeric) return an - bn;
			if (aNumeric) return -1;
			if (bNumeric) return 1;
			return av.localeCompare(bv);
		},
		[normalizeVersionCode],
	);

	const getNextNumericVersion = useCallback((versions) => {
		const normalized = (versions || []).map((v) => String(v || "").trim()).filter(Boolean);
		const existingSet = new Set(normalized);
		const numericVersions = normalized
			.map((v) => Number.parseInt(v, 10))
			.filter((n) => Number.isInteger(n) && n > 0);

		let next = numericVersions.length > 0 ? Math.max(...numericVersions) + 1 : 1;
		while (existingSet.has(String(next))) {
			next += 1;
		}
		return String(next);
	}, []);

	const buildStudentSignatureSetFromDb = useCallback((students) => {
		const out = new Set();
		for (const stu of students || []) {
			const seq = String(stu?.sheet_sequence || "").trim();
			const stdId = String(stu?.std_id_raw || "")
				.trim()
				.toUpperCase();
			const stdName = String(stu?.std_name_raw || "")
				.trim()
				.toUpperCase()
				.split(/\s+/)
				.join(" ");
			const identity = stdId || stdName;
			if (seq && identity) {
				out.add(`${seq}|${identity}`);
			}
		}
		return out;
	}, []);

	const buildStudentSignatureSetFromUpload = useCallback((parsed) => {
		const out = new Set();
		for (const stu of parsed?.studentResponses || []) {
			const seq = String(stu?.sheet_sequence || "").trim();
			const stdId = String(stu?.id_number || "")
				.trim()
				.toUpperCase();
			const stdName = String(stu?.name || "")
				.trim()
				.toUpperCase()
				.split(/\s+/)
				.join(" ");
			const identity = stdId || stdName;
			if (seq && identity) {
				out.add(`${seq}|${identity}`);
			}
		}
		return out;
	}, []);

	const buildDbKeySignatures = useCallback(
		(header, keys) => {
			const lq = parseInt(header?.lqnum, 10) || 1;
			const hq = parseInt(header?.hqnum, 10) || lq;
			const count = Math.max(0, hq - lq + 1);
			const map = new Map();

			for (const key of keys || []) {
				const fallback = (keys || []).length > 1 ? "" : "1";
				const version = normalizeVersionCode(key?.version, fallback);
				const byItemNo = new Map();
				for (const item of key?.items || []) {
					byItemNo.set(Number(item?.item_no), item);
				}

				const tokens = [];
				for (let i = 0; i < count; i++) {
					const qNo = lq + i;
					const item = byItemNo.get(qNo);
					if (!item || String(item?.omit || "N").toUpperCase() === "Y") {
						tokens.push(" ");
						continue;
					}
					const answers = Array.isArray(item?.original_answer)
						? item.original_answer
						: Array.isArray(item?.modified_answer)
							? item.modified_answer
							: [];
					const normalizedAnswers = answers
						.map((a) =>
							String(a || "")
								.trim()
								.toUpperCase(),
						)
						.filter(Boolean)
						.sort();
					tokens.push(normalizedAnswers.length > 0 ? normalizedAnswers.join(",") : " ");
				}

				map.set(version, tokens.join("|"));
			}

			return map;
		},
		[normalizeVersionCode],
	);

	const buildUploadKeySignatures = useCallback(
		(parsed) => {
			const lq = parseInt(parsed?.header?.lqnum, 10) || 1;
			const hq = parseInt(parsed?.header?.hqnum, 10) || lq;
			const count = Math.max(0, hq - lq + 1);
			const nkeys = parseInt(parsed?.header?.nkeys, 10) || 1;
			const map = new Map();

			for (const key of parsed?.answerKeys || []) {
				const fallback = nkeys > 1 ? "" : "1";
				const version = nkeys > 1 ? normalizeVersionCode(key?.data?.special_code, fallback) : "1";
				const answers = String(key?.data?.answers_chosen || "");
				const tokens = [];
				for (let i = 0; i < count; i++) {
					const answer = String(answers[i] || "")
						.trim()
						.toUpperCase();
					tokens.push(answer || " ");
				}
				map.set(version, tokens.join("|"));
			}

			return map;
		},
		[normalizeVersionCode],
	);

	const determineUploadDecision = useCallback(
		(parsed) => {
			const dbStudents = examProcessingState?.students || [];
			const dbKeys = examProcessingState?.answer_keys || [];

			const dbStudentSet = buildStudentSignatureSetFromDb(dbStudents);
			const uploadStudentSet = buildStudentSignatureSetFromUpload(parsed);
			const studentsMatch =
				dbStudentSet.size > 0 &&
				dbStudentSet.size === uploadStudentSet.size &&
				[...dbStudentSet].every((sig) => uploadStudentSet.has(sig));

			const dbKeyMap = buildDbKeySignatures(examProcessingState?.header, dbKeys);
			const uploadKeyMap = buildUploadKeySignatures(parsed);

			const parsedNKeys = parseInt(parsed?.header?.nkeys, 10) || 1;
			const multiUpload = parsedNKeys > 1 || uploadKeyMap.size > 1;
			const multiDb = dbKeyMap.size > 1;
			const uploadKeysMatchExistingDbKeys =
				uploadKeyMap.size > 0 &&
				[...uploadKeyMap.entries()].every(
					([version, sig]) => dbKeyMap.has(version) && dbKeyMap.get(version) === sig,
				);

			let keysMatch = false;
			if (multiUpload || multiDb) {
				keysMatch = uploadKeysMatchExistingDbKeys;
			} else {
				const dbSig = [...dbKeyMap.values()][0] || "";
				const uploadSig = [...uploadKeyMap.values()][0] || "";
				keysMatch = dbSig === uploadSig;
			}

			const existingVersions = [...dbKeyMap.keys()]
				.map((v) => normalizeVersionCode(v, "1"))
				.filter(Boolean);
			const isSingleKeyUpload =
				(parsedNKeys === 1 && (parsed?.answerKeys?.length || 0) === 1) || false;
			const singleKeyAppendWillCreateNewVersion = isSingleKeyUpload && existingVersions.length > 0;
			const suggestedAppendVersion = singleKeyAppendWillCreateNewVersion
				? getNextNumericVersion(existingVersions)
				: null;

			const decisionMeta = {
				existingVersions,
				singleKeyAppendWillCreateNewVersion,
				suggestedAppendVersion,
			};

			if (studentsMatch) {
				return {
					scenario: "replace_both",
					studentsMatch,
					keysMatch,
					...decisionMeta,
				};
			}

			if (!studentsMatch && keysMatch) {
				return {
					scenario: "append_students",
					studentsMatch,
					keysMatch,
					...decisionMeta,
				};
			}

			return {
				scenario: "append_or_replace_both",
				studentsMatch,
				keysMatch,
				...decisionMeta,
			};
		},
		[
			examProcessingState?.students,
			examProcessingState?.answer_keys,
			examProcessingState?.header,
			buildStudentSignatureSetFromDb,
			buildStudentSignatureSetFromUpload,
			buildDbKeySignatures,
			buildUploadKeySignatures,
			getNextNumericVersion,
			normalizeVersionCode,
		],
	);

	const handlePrint = async (pulledReqnum) => {
		// Build a new document with the latest state
		const docObject = {
			lqNum: examProcessingState?.header.lqnum,
			hqNum: examProcessingState?.header.hqnum,
			qLen: examProcessingState?.header.qlen,
			nKeys: examProcessingState?.header.nkeys,
			...currentOmrRequest,
		};
		const doc = buildDoc(docObject, pulledReqnum);

		// Optionally, if you want to auto-print:
		doc.autoPrint();

		// Save the PDF (this will trigger a download)
		doc.save("Test Scoring Receipt " + pulledReqnum + ".pdf");
	};

	const responses = useMemo(
		() => (examProcessingState?.students.length > 0 ? examProcessingState.students : []),
		[examProcessingState],
	);

	const matchedStudents = useMemo(
		() =>
			responses?.filter(
				(stu) =>
					(stu.match_attempted === "Y" || stu.matched) &&
					stu.std_id_matched &&
					stu.std_name_matched,
			),
		[responses],
	);

	const unmatchedStudents = useMemo(
		() =>
			responses.filter(
				(stu) =>
					((stu.match_attempted === "Y" && stu.match_type === "cannot-match") ||
						!stu.match_attempted) &&
					(!stu.std_id_matched || stu.std_id_matched === "") &&
					(!stu.std_name_matched || stu.std_name_matched === ""),
			),
		[responses],
	);

	const answerKeyVersions = useMemo(
		() =>
			Array.isArray(examProcessingState?.answer_keys)
				? examProcessingState.answer_keys
				: Object.values(examProcessingState?.answer_keys || {}),
		[examProcessingState?.answer_keys],
	);

	const focusWorkflowSection = useCallback(
		(sectionKey = "exam-info") => {
			const requiresUploadedData = sectionKey !== "exam-info";
			if (requiresUploadedData && !examInDatabase) {
				setAlignment("exam-info");
				examInfoSectionRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				setSnackbar({
					open: true,
					message:
						"Upload the raw scanner data file in Step 1 before reviewing answer keys or results.",
					severity: "info",
				});
				return;
			}

			setAlignment(sectionKey);

			const sectionRef =
				sectionKey === "keys-and-stats"
					? keysAndStatsSectionRef
					: sectionKey === "class-and-scores"
						? classAndScoresSectionRef
						: examInfoSectionRef;

			sectionRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		},
		[examInDatabase],
	);

	// ------- Handlers / callbacks (stable) -------

	const [fetchingRequestDetails, setFetchingRequestDetails] = useState(false);

	const fetchRequestDetails = useCallback(
		async (requestedReqnum = null) => {
			const targetReqnum = String(requestedReqnum || activeReqnumRef.current || "").trim();
			if (!targetReqnum) return;
			setFetchingRequestDetails(true);
			try {
				const response = await getCompleteGradingRequest(token, targetReqnum);
				if (activeReqnumRef.current !== targetReqnum) return;
				const {
					responses: responseRows = [],
					keys: keyRows = [],
					mod_logs: _modLogs,
					...requestFields
				} = response || {};

				setCurrentOmrRequest((current) => {
					if (!current || String(current.reqnum) !== targetReqnum) {
						return current;
					}
					return {
						...current,
						...requestFields,
					};
				});
				setOmrRequests((currentRequests) =>
					currentRequests.map((requestItem) =>
						String(requestItem?.reqnum) === targetReqnum
							? {
									...requestItem,
									...requestFields,
								}
							: requestItem,
					),
				);

				if (
					response &&
					response.lqnum &&
					response.hqnum &&
					response.qlen &&
					response.nkeys &&
					response.reqnum &&
					responseRows &&
					responseRows.length > 0 &&
					keyRows &&
					keyRows.length > 0 &&
					String(response.reqnum) === targetReqnum
				) {
					const nextExamProcessingState = {
						header: {
							lqnum: response.lqnum,
							hqnum: response.hqnum,
							qlen: response.qlen,
							nkeys: response.nkeys,
							reqnum: response.reqnum,
							a: response.a,
							b: response.b,
							c: response.c,
							d: response.d,
							e: response.e,
							f: response.f,
							k: response.k,
							l: response.l,
							m: response.m,
							n: response.n,
							o: response.o,
							p: response.p,
							coursetitle: response.coursetitle,
						},
						students: responseRows || [],
						answer_keys:
							keyRows.length > 1
								? keyRows.sort((a, b) => compareVersionCodes(a.version, b.version))
								: keyRows || [],
					};
					setExamProcessingState(nextExamProcessingState);
					setExamInDatabase(true);
					return nextExamProcessingState;
				} else {
					setExamProcessingState(null);
					setExamInDatabase(false);
					return null;
				}
			} catch (error) {
				if (activeReqnumRef.current !== targetReqnum) return;
				console.error("Failed to fetch request details:", error);
				setExamProcessingState(null);
				setExamInDatabase(false);
				return null;
			} finally {
				if (activeReqnumRef.current === targetReqnum) {
					setFetchingRequestDetails(false);
				}
			}
		},
		[
			token,
			setCurrentOmrRequest,
			setOmrRequests,
			setExamProcessingState,
			setExamInDatabase,
			compareVersionCodes,
		],
	);

	const refreshActionsDisabled = Boolean(examFileUploading || editMode || openReconciliationDialog);

	const handleRefreshCurrentRequest = useCallback(async () => {
		const targetReqnum = String(currentOmrRequest?.reqnum || "").trim();
		if (!targetReqnum) return;
		await fetchRequestDetails(targetReqnum);
		await getClassFileEarly();
	}, [currentOmrRequest?.reqnum, fetchRequestDetails, getClassFileEarly]);

	const handleClickToCopyEmail = useCallback(() => {
		if (currentOmrRequest?.email1) {
			navigator.clipboard.writeText(currentOmrRequest.email1);
			setCopyClicked(true);
			setTimeout(() => setCopyClicked(false), 2000);
		}
	}, [currentOmrRequest?.email1]);

	const handleClickToModifyClassID = () => {
		setClassIDInputValue(classId || "");
		setClassIdError("");
		setModifyingClassID(!modifyingClassID);
		/* if (classId) {
			setCopyClicked(true);
			navigator.clipboard.writeText(classId);
			setTimeout(() => setCopyClicked(false), 2000);
		} */
	};

	const handleClickToCopyReqnum = useCallback(() => {
		if (currentOmrRequest?.reqnum) {
			navigator.clipboard.writeText(currentOmrRequest.reqnum);
			setCopyClicked(true);
			setTimeout(() => setCopyClicked(false), 2000);
		}
	}, [currentOmrRequest?.reqnum]);

	const handleUploadFile = useCallback(
		async (e) => {
			const f = e.target.files[0];
			if (!f) {
				setFile(f);
				return;
			}
			if (inputSettings.multipleFiles) {
				// Handle multiple files if you want to support that in the future
				// You would need to loop through files and process each one, potentially with additional UI for tracking
				console.warn("Multiple file upload not implemented yet.");
				return;
			} else {
				setExamFileUploading(true);
				setFile(f);

				if (f.size > 0) {
					const reader = new FileReader();
					reader.onload = async (event) => {
						const text = event.target.result;

						// Parse the file on the frontend first
						try {
							const parsed = parseFile(text, {
								includeAnswerKeyDicts: false,
								includeWeightedKeyMaps: false,
								assignScores: false,
								includeScoresForEachKeyVersion: false,
							});

							// Check if reqnum matches the selected request
							const reqnumFromFile = parsed.header?.reqnum;
							if (Number(reqnumFromFile) !== Number(currentOmrRequest.reqnum)) {
								setSnackbar({
									open: true,
									message: `File reqnum (${reqnumFromFile}) does not match selected request (${currentOmrRequest.reqnum}).`,
									severity: "error",
								});
								setExamFileUploading(false);
								setFile(null);
								return;
							}

							// Store parsed data and original content for the dialog
							setParsedFileData(parsed);
							setOriginalFileContent(text);
							setReqnumFromResponse(reqnumFromFile);

							if (examInDatabase) {
								const decision = determineUploadDecision(parsed);
								setPendingReuploadData(decision);
								setOpenReuploadConfirmDialog(true);
							} else {
								setSelectedUploadMode("upsert");
								// No existing data; go straight to validation dialog
								setOpenFileValidationDialog(true);
							}
						} catch (error) {
							console.error("Error parsing file on frontend:", error);
							setSnackbar({
								open: true,
								message: `Error parsing file: ${error.message || "Unknown error"}`,
								severity: "error",
							});
							setFile(null);
						}

						setExamFileUploading(false);
					};
					reader.readAsText(f);
				} else {
					setExamFileUploading(false);
				}
			}
		},
		[
			currentOmrRequest?.reqnum,
			inputSettings.multipleFiles,
			examInDatabase,
			determineUploadDecision,
		],
	);

	const handleProceedWithUploadMode = useCallback((mode) => {
		setSelectedUploadMode(mode);
		setOpenReuploadConfirmDialog(false);
		setPendingReuploadData(null);
		setOpenFileValidationDialog(true);
	}, []);

	// Handler for cancelling re-upload
	const handleCancelReupload = useCallback(() => {
		setOpenReuploadConfirmDialog(false);
		setPendingReuploadData(null);
		setSelectedUploadMode("upsert");
		setFile(null);
	}, []);

	// Callback for when file validation dialog completes upload successfully
	const handleFileUploadComplete = useCallback(async () => {
		await fetchRequestDetails();
		window.setTimeout(() => {
			setAlignment("keys-and-stats");
			keysAndStatsSectionRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 0);
	}, [fetchRequestDetails]);

	const studentSectionLookup = useMemo(
		() => buildStudentSectionLookup(classListFile?.classlist || []),
		[classListFile?.classlist],
	);

	const buildCsvRowsMemo = useCallback(
		(matchedList, unmatchedList, outputSettings) => {
			const keysArr = Array.isArray(examProcessingState.answer_keys)
				? examProcessingState.answer_keys
				: Object.values(examProcessingState.answer_keys || {});
			const includeWeightBreakdown =
				Boolean(outputSettings?.includeWeightBreakdownColumns) && isWeightedExam(keysArr);
			const weightBreakdownValues = includeWeightBreakdown
				? getUniqueWeightBreakdownValues(keysArr)
				: [];
			const buildWeightBreakdownColumns = (weightBreakdown = {}) =>
				weightBreakdownValues.reduce((acc, weightValue) => {
					acc[`Weight ${weightValue}`] = Number((weightBreakdown?.[weightValue] || 0).toFixed(3));
					return acc;
				}, {});

			const km = buildKeyMapByVersion(keysArr);
			const knownVersions = normalizeVersionListForScoring(keysArr.map((key) => key?.version));
			const resolveVersionForStudent = (student) =>
				resolveStudentVersionForScoring(student, knownVersions, "single").resolvedVersion ||
				"single";
			const matchedRows = Array.isArray(matchedList) ? matchedList : [];
			const unmatchedRows = Array.isArray(unmatchedList) ? unmatchedList : [];
			const mkMatched = matchedRows
				.map((stu) => {
					const version = resolveVersionForStudent(stu);
					const answerSetByQ = km[version] || km.single || {};
					const resp = stu.std_response ?? stu.response ?? stu.std_response_str ?? "";
					const keyObj =
						keysArr.find((k) => String(k?.version?.trim?.() || "single") === version) || keysArr[0];

					const { score, wrongStr, scoredStr, weightBreakdown } = gradeResponseString(
						resp,
						answerSetByQ,
						keyObj,
						examProcessingState?.header,
						outputSettings,
					);

					const full = (stu.std_name_matched ?? "").trim();
					const parts = full ? full.split(/\s+/) : [];
					const first = parts.length ? parts.at(-1) : "";
					const last = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
					const section =
						studentSectionLookup.get(normalizeStudentIdentifier(stu.std_id_matched)) || "";

					return {
						"Last Name": last,
						"First Name": first,
						"Student No": stu.std_id_matched,
						CCID: stu.ccid || "",
						Section: section,
						"OMR Score": score,
						Version: version,
						"Items Wrong": wrongStr,
						"Scored Responses": scoredStr,
						...buildWeightBreakdownColumns(weightBreakdown),
					};
				})
				.sort((a, b) => {
					const lastCmp = a["Last Name"].localeCompare(b["Last Name"]);
					return lastCmp !== 0 ? lastCmp : a["First Name"].localeCompare(b["First Name"]);
				});

			const mkUnmatched = unmatchedRows
				.map((stu) => {
					const version = resolveVersionForStudent(stu);
					const answerSetByQ = km[version] || km.single || {};
					const resp = stu.std_response ?? stu.response ?? stu.std_response_str ?? "";
					const keyObj =
						keysArr.find((k) => String(k?.version?.trim?.() || "single") === version) || keysArr[0];

					const { score, wrongStr, scoredStr, weightBreakdown } = gradeResponseString(
						resp,
						answerSetByQ,
						keyObj,
						examProcessingState?.header,
						outputSettings,
					);

					return {
						"Last Name": stu.std_name_raw || "(Missing)",
						"First Name": "(Missing)",
						"Student No": stu.std_id_raw?.trim().length > 0 ? stu.std_id_raw : "(Missing)",
						CCID: "(Missing)",
						Section: "",
						"OMR Score": score,
						Version: version,
						"Items Wrong": wrongStr,
						"Scored Responses": scoredStr,
						...buildWeightBreakdownColumns(weightBreakdown),
					};
				})
				.sort((a, b) => {
					const lastCmp = a["Last Name"].localeCompare(b["Last Name"]);
					return lastCmp !== 0 ? lastCmp : a["Student No"].localeCompare(b["Student No"]);
				});

			if (gradeWithoutMatching) return [...mkUnmatched];
			return [...mkMatched, ...mkUnmatched];
		},
		[examProcessingState, gradeWithoutMatching, studentSectionLookup],
	);

	const appendSuffixToFilename = useCallback((filename, filenameSuffix = "") => {
		if (!filenameSuffix || !filename) return filename;
		const dotIndex = filename.lastIndexOf(".");
		if (dotIndex <= 0) return `${filename}${filenameSuffix}`;
		return `${filename.slice(0, dotIndex)}${filenameSuffix}${filename.slice(dotIndex)}`;
	}, []);

	const canvasHeaders = useMemo(
		() => [
			"Student",
			"ID",
			"SIS User ID",
			"SIS Login ID",
			"Section",
			"Grades",
			"Version",
			"Items Wrong",
			"Scored Responses",
		],
		[],
	);

	const legacySpreadsheetHeaders = useMemo(
		() => [
			"Last Name",
			"First Name",
			"Student No",
			"CCID",
			"OMR Score",
			"Version",
			"Items Wrong",
			"Scored Responses",
		],
		[],
	);

	const getSpreadsheetWeightHeaders = useCallback((rows) => {
		const seen = new Set();
		for (const row of rows || []) {
			for (const key of Object.keys(row || {})) {
				if (key.startsWith("Weight ")) {
					seen.add(key);
				}
			}
		}
		return [...seen].sort((left, right) => {
			const leftNumber = Number(left.replace("Weight ", ""));
			const rightNumber = Number(right.replace("Weight ", ""));
			if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
				return leftNumber - rightNumber;
			}
			return left.localeCompare(right);
		});
	}, []);

	const buildCanvasRows = useCallback(
		(rows, weightHeaders = []) =>
			(rows || []).map((r) => ({
				Student: `${r["Last Name"]}, ${r["First Name"]}`,
				ID: r["Student No"],
				"SIS User ID": r["Student No"],
				"SIS Login ID": r.CCID,
				Section: r.Section || "",
				Grades: r["OMR Score"],
				Version: formatExcelTextCell(r.Version),
				"Items Wrong": r["Items Wrong"],
				"Scored Responses": r["Scored Responses"],
				...weightHeaders.reduce((acc, header) => {
					acc[header] = Number((r[header] || 0).toFixed(3));
					return acc;
				}, {}),
			})),
		[],
	);

	const buildLegacySpreadsheetRows = useCallback(
		(rows) =>
			(rows || []).map((row) => ({
				...row,
				Version: formatExcelTextCell(row.Version),
			})),
		[],
	);

	const getSpreadsheetExportConfig = useCallback(
		(rows, effectiveOutputSettings) => {
			const useLegacyFormat = Boolean(effectiveOutputSettings?.oldExamResultFormat);
			const spreadsheetWeightHeaders = getSpreadsheetWeightHeaders(rows);
			return {
				filenameStem: useLegacyFormat ? "w" : "canvasUpload",
				headers: useLegacyFormat
					? [...legacySpreadsheetHeaders, ...spreadsheetWeightHeaders]
					: [...canvasHeaders, ...spreadsheetWeightHeaders],
				rows: useLegacyFormat
					? buildLegacySpreadsheetRows(rows)
					: buildCanvasRows(rows, spreadsheetWeightHeaders),
			};
		},
		[
			legacySpreadsheetHeaders,
			canvasHeaders,
			buildLegacySpreadsheetRows,
			buildCanvasRows,
			getSpreadsheetWeightHeaders,
		],
	);

	const triggerBlobDownload = useCallback((blob, filename) => {
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}, []);

	const handleDownloadCsv = useCallback(
		({ filenameSuffix = "", outputSettings: outputOverride } = {}) => {
			if (!examProcessingState) return;
			const effectiveOutputSettings = outputOverride || outputSettings;

			const combinedRows = buildCsvRowsMemo(
				matchedStudents,
				unmatchedStudents,
				effectiveOutputSettings,
			);
			const {
				filenameStem,
				headers: spreadsheetHeaders,
				rows: spreadsheetRows,
			} = getSpreadsheetExportConfig(combinedRows, effectiveOutputSettings);
			const matchedSpreadsheetRows = getSpreadsheetExportConfig(
				buildCsvRowsMemo(matchedStudents, [], effectiveOutputSettings),
				effectiveOutputSettings,
			).rows;
			const unmatchedSpreadsheetRows = getSpreadsheetExportConfig(
				buildCsvRowsMemo([], unmatchedStudents, effectiveOutputSettings),
				effectiveOutputSettings,
			).rows;

			const rootClassIds = parseRootClassIds(currentOmrRequest?.class_ids);
			const splitBySection = Boolean(
				effectiveOutputSettings?.downloadPerClassSection && rootClassIds.length > 1,
			);

			if (splitBySection) {
				let sectionFileCount = 0;
				for (const classId of rootClassIds) {
					const sectionRows = matchedSpreadsheetRows.filter(
						(row) => String(row.Section || "") === String(classId),
					);
					if (sectionRows.length === 0) continue;

					const sectionCsv = toCsv(spreadsheetHeaders, sectionRows);
					const sectionBlob = new Blob([sectionCsv], { type: "text/csv;charset=utf-8;" });
					triggerBlobDownload(
						sectionBlob,
						appendSuffixToFilename(
							`${currentOmrRequest?.reqnum}_${filenameStem}_${classId}.csv`,
							filenameSuffix,
						),
					);
					sectionFileCount += 1;
				}

				if (unmatchedSpreadsheetRows.length > 0) {
					const unmatchedCsv = toCsv(spreadsheetHeaders, unmatchedSpreadsheetRows);
					const unmatchedBlob = new Blob([unmatchedCsv], { type: "text/csv;charset=utf-8;" });
					triggerBlobDownload(
						unmatchedBlob,
						appendSuffixToFilename(`${currentOmrRequest?.reqnum}_Unmatched.csv`, filenameSuffix),
					);
				}

				if (sectionFileCount > 0 || unmatchedSpreadsheetRows.length > 0) return;
			}

			const spreadsheetCsv = toCsv(spreadsheetHeaders, spreadsheetRows);
			const spreadsheetBlob = new Blob([spreadsheetCsv], { type: "text/csv;charset=utf-8;" });
			triggerBlobDownload(
				spreadsheetBlob,
				appendSuffixToFilename(`${currentOmrRequest?.reqnum}_${filenameStem}.csv`, filenameSuffix),
			);
		},
		[
			examProcessingState,
			matchedStudents,
			unmatchedStudents,
			currentOmrRequest?.class_ids,
			currentOmrRequest?.reqnum,
			buildCsvRowsMemo,
			getSpreadsheetExportConfig,
			outputSettings,
			appendSuffixToFilename,
			triggerBlobDownload,
		],
	);

	const getCsvBlobs = useCallback(
		({ examProcessingState: eps, reqnum, outputSettings: outputOverride, filenameSuffix = "" }) => {
			if (!eps) return [];
			const effectiveOutputSettings = outputOverride || outputSettings;

			const matched = (eps.students || []).filter(
				(stu) =>
					(stu.match_attempted === "Y" || stu.matched) &&
					stu.std_id_matched &&
					stu.std_name_matched,
			);
			const unmatched = (eps.students || []).filter(
				(stu) =>
					(stu.match_attempted === "Y" || stu.cannot_match || !stu.match_attempted) &&
					!stu.std_id_matched &&
					!stu.std_name_matched,
			);
			const rows = buildCsvRowsMemo(matched, unmatched, effectiveOutputSettings);
			const {
				filenameStem,
				headers: spreadsheetHeaders,
				rows: spreadsheetRows,
			} = getSpreadsheetExportConfig(rows, effectiveOutputSettings);
			const matchedSpreadsheetRows = getSpreadsheetExportConfig(
				buildCsvRowsMemo(matched, [], effectiveOutputSettings),
				effectiveOutputSettings,
			).rows;
			const unmatchedSpreadsheetRows = getSpreadsheetExportConfig(
				buildCsvRowsMemo([], unmatched, effectiveOutputSettings),
				effectiveOutputSettings,
			).rows;
			const resolvedReqnum = reqnum || currentOmrRequest?.reqnum || "exam";
			const rootClassIds = parseRootClassIds(currentOmrRequest?.class_ids);
			const splitBySection = Boolean(
				effectiveOutputSettings?.downloadPerClassSection && rootClassIds.length > 1,
			);

			if (splitBySection) {
				const sectionEntries = rootClassIds
					.map((classId) => {
						const sectionRows = matchedSpreadsheetRows.filter(
							(row) => String(row.Section || "") === String(classId),
						);
						if (sectionRows.length === 0) return null;
						const sectionCsv = toCsv(spreadsheetHeaders, sectionRows);
						return {
							name: appendSuffixToFilename(
								`${resolvedReqnum}_${filenameStem}_${classId}.csv`,
								filenameSuffix,
							),
							blob: new Blob([sectionCsv], { type: "text/csv;charset=utf-8" }),
						};
					})
					.filter(Boolean);

				if (unmatchedSpreadsheetRows.length > 0) {
					const unmatchedCsv = toCsv(spreadsheetHeaders, unmatchedSpreadsheetRows);
					sectionEntries.push({
						name: appendSuffixToFilename(`${resolvedReqnum}_Unmatched.csv`, filenameSuffix),
						blob: new Blob([unmatchedCsv], { type: "text/csv;charset=utf-8" }),
					});
				}

				if (sectionEntries.length > 0) return sectionEntries;
			}

			const spreadsheetCsv = toCsv(spreadsheetHeaders, spreadsheetRows);
			const spreadsheetBlob = new Blob([spreadsheetCsv], { type: "text/csv;charset=utf-8" });
			return [
				{
					name: appendSuffixToFilename(`${resolvedReqnum}_${filenameStem}.csv`, filenameSuffix),
					blob: spreadsheetBlob,
				},
			];
		},
		[
			buildCsvRowsMemo,
			getSpreadsheetExportConfig,
			appendSuffixToFilename,
			outputSettings,
			currentOmrRequest?.reqnum,
			currentOmrRequest?.class_ids,
		],
	);

	const handleRescoreExam = useCallback(async (sourceExamProcessingState = examProcessingState) => {
		if (!sourceExamProcessingState) return null;

		// Normalize answer_keys to an array for the builder
		const keysArr = Array.isArray(sourceExamProcessingState.answer_keys)
			? sourceExamProcessingState.answer_keys
			: Object.values(sourceExamProcessingState.answer_keys || {});

		const knownVersions = normalizeVersionListForScoring(keysArr.map((key) => key?.version));
		const km = buildKeyMapByVersion(keysArr); // { version: { q: Set(...) } }
		const sourceResponses = sourceExamProcessingState?.students || [];

		const next = sourceResponses.map((stu) => {
			const version =
				resolveStudentVersionForScoring(stu, knownVersions, "single").resolvedVersion || "single";
			const answerSetByQ = km[version] || km["single"] || {};

			// Common response field names I’ve seen in your app
			const resp = stu.std_response ?? stu.response ?? stu.std_response_str ?? "";

			const keyObj =
				keysArr.find((k) => String(k?.version?.trim?.() || "single") === version) || keysArr[0];

			const { score, wrongStr, scoredStr } = gradeResponseString(
				resp,
				answerSetByQ,
				keyObj,
				sourceExamProcessingState?.header,
				outputSettings,
			);

			return {
				...stu,
				modified_score: score,
				items_wrong: wrongStr,
				scored_responses: scoredStr,
			};
		});

		const nextExamProcessingState = {
			...sourceExamProcessingState,
			students: next,
		};

		setExamProcessingState(nextExamProcessingState);
		return nextExamProcessingState;
	}, [examProcessingState, setExamProcessingState, outputSettings]);

	const examDataPending = Boolean(
		(examFileUploading || openFileValidationDialog || fetchingRequestDetails) && !examInDatabase,
	);

	const allResponsesAttempted = useMemo(
		() => responses.length > 0 && responses.every((student) => student.match_attempted === "Y"),
		[responses],
	);

	const matchingProgressLabel = useMemo(() => {
		if (!examInDatabase) return examDataPending ? "Waiting for upload processing" : "Locked";
		if (responses.length === 0) return "No student responses";
		if (gradeWithoutMatching) return "Downloads enabled without matching";
		if (allResponsesAttempted && unmatchedStudents.length === 0) return "Matching complete";
		if (matchedStudents.length > 0) return `${unmatchedStudents.length} unmatched`;
		return "Needs student matching";
	}, [
		examInDatabase,
		examDataPending,
		responses.length,
		gradeWithoutMatching,
		allResponsesAttempted,
		unmatchedStudents.length,
		matchedStudents.length,
	]);

	const workflowSections = useMemo(
		() => [
			{
				id: "exam-info",

				label: "Request + Upload",

				status: examInDatabase ? "Ready" : examFileUploading ? "Uploading" : "Required",
				icon: <TextSnippetIcon fontSize="large" color="primary" />,
			},
			{
				id: "keys-and-stats",

				label: "Answer Keys + Statistics",

				status: examInDatabase
					? `${answerKeyVersions.length || 0} key version${
							answerKeyVersions.length === 1 ? "" : "s"
						}`
					: "Locked",
				icon: <LegendToggleIcon fontSize="large" />,
			},
			{
				id: "class-and-scores",

				label: "Matching + Results",

				status: matchingProgressLabel,
				icon: <JoinLeftIcon fontSize="large" />,
			},
			{
				id: "download-results",
				label: "Download",

				status: examInDatabase ? "Ready" : "Locked",
				icon: <DownloadForOfflineIcon fontSize="large" />,
			},
		],
		[examInDatabase, examFileUploading, answerKeyVersions.length, matchingProgressLabel],
	);

	// ------- Effects -------
	useEffect(() => {
		if (currentOmrRequest?.reqnum) fetchRequestDetails(currentOmrRequest.reqnum);
	}, [currentOmrRequest?.reqnum, fetchRequestDetails]);

	useEffect(() => {
		if (responses.length > 0) {
			setAllStudentsMatched(responses.every((s) => s.match_attempted === "Y"));
		}
	}, [responses, setAllStudentsMatched]);

	useEffect(() => {
		if (currentOmrRequest?.reqnum) {
			setPulse(true);
			const t = setTimeout(() => setPulse(false), 1000);
			return () => clearTimeout(t);
		}
	}, [currentOmrRequest?.reqnum]);

	useEffect(() => {
		if (!currentOmrRequest?.reqnum) return;
		window.setTimeout(() => {
			examInfoSectionRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 0);
	}, [currentOmrRequest?.reqnum]);

	const normalizeClassIds = useCallback((raw) => {
		const cleaned = (raw || "").replace(/\s+/g, "");
		if (!cleaned) return { value: "", error: "" };

		const parts = cleaned.split(",").filter(Boolean);
		const invalid = parts.filter((p) => !/^\d{5}$/.test(p));

		if (invalid.length > 0) {
			return {
				value: cleaned,
				error: `Class IDs must be 5 digits. Check: ${invalid.join(", ")}`,
			};
		}

		// dedupe while preserving order
		const deduped = [...new Set(parts)];
		return { value: deduped.join(","), error: "" };
	}, []);

	const handleChangeModifyClassIDInputValue = useCallback(
		(e) => {
			const { value, error } = normalizeClassIds(e.target.value);
			setClassIDInputValue(value);
			setClassIdError(error);
		},
		[normalizeClassIds],
	);
	// ------- Render -------

	const handleRefreshSpecificRequest = async (reqnum) => {
		if (String(reqnum).length === 6) {
			try {
				const response = await getCompleteGradingRequest(token, String(reqnum));

				if (response && response.data && response.data.length > 0) {
					//replace the specific request in the omrRequests list
					setOmrRequests((prev) => {
						const filtered = prev.filter((reqObj) => String(reqObj.reqnum) !== String(reqnum));
						return [response.data[0], ...filtered];
					});
				} else {
					console.error("No data found for the specific request.");
				}
			} catch (err) {
				console.error("Error fetching specific request:", err);
			}
		}
	};

	const handleUpdateReqClassID = async () => {
		const { value, error } = normalizeClassIds(classIDInputValue);
		if (error || !value) {
			setClassIdError(error || "Please enter at least one 5-digit class ID.");
			return;
		}
		setClassIdError("");

		setLoadingUpdateClassID(true);
		try {
			// 1) Update the request on the backend
			await updateExamReqClassID(token, currentOmrRequest.reqnum, value);

			setClassId(value);
			setClassIDInputValue(value);
			await handleRefreshSpecificRequest(currentOmrRequest.reqnum);

			//TODO: CHECK THIS await handleRescoreExam();
		} catch (error) {
			console.error("Failed to update currentOmrRequest class id:", error);
		} finally {
			setLoadingUpdateClassID(false);
			setModifyingClassID(false);
		}
	};

	const getDeleteSuccessMessage = (deleteMode) => {
		if (deleteMode === DELETE_MODE.KEYS)
			return "Exam keys and modification logs deleted successfully.";
		if (deleteMode === DELETE_MODE.STUDENTS) return "Student responses deleted successfully.";
		return "Exam keys, student responses, and modification logs deleted successfully.";
	};

	const handleDeleteExamRecords = async (deleteMode) => {
		if (!currentOmrRequest?.reqnum) return;

		setLoadingDelete(true);
		try {
			await deleteExamRecords(token, currentOmrRequest.reqnum, deleteMode);

			// Wipe all exam-related state clean via RESET
			setExamProcessingState(RESET);
			setExamInDatabase(RESET);
			setAllStudentsMatched(RESET);
			setAlignment("exam-info");
			setEditMode(false);
			setFile(null);
			setParsedFileData(null);
			setOriginalFileContent(null);
			setReqnumFromResponse(null);
			setReconciliationState([]);
			setOpenReconciliationDialog(false);

			// Refresh the request details
			await handleRefreshSpecificRequest(currentOmrRequest.reqnum);

			setSnackbar({
				open: true,
				message: getDeleteSuccessMessage(deleteMode),
				severity: "success",
			});
		} catch (error) {
			console.error("Failed to delete exam records:", error);
			setSnackbar({
				open: true,
				message: "Failed to delete exam records. Please try again.",
				severity: "error",
			});
		} finally {
			setLoadingDelete(false);
			setOpenDeleteDialog(false);
		}
	};

	const renderLockedWorkflowSection = (title, message) => (
		<div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-8 shadow-sm">
			<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
				<div className="max-w-3xl">
					<p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-400">
						{examDataPending ? "Preparing exam data" : "Locked until Step 1 is complete"}
					</p>
					<h4 className="mt-2 text-lg font-semibold text-darkGreen">{title}</h4>
					<p className="mt-2 text-sm text-gray-600">
						{examDataPending
							? "The uploaded file is being validated and loaded. This section will unlock automatically once processing finishes."
							: message}
					</p>
				</div>
				<Button variant="outlined" onClick={() => focusWorkflowSection("exam-info")}>
					Return to Upload
				</Button>
			</div>
		</div>
	);

	return (
		<div className=" w-full bg-digitalWhite  ">
			<div className=" w-full flex border-x relative ">
				<ReconciliationDialog
					open={openReconciliationDialog}
					setOpen={setOpenReconciliationDialog}
					reconciliationState={reconciliationState}
					setReconciliationState={setReconciliationState}
					reqnum={reqnumFromResponse}
					token={token}
					file={file}
					fetchRequestDetails={fetchRequestDetails}
					ccid={ccid}
				/>

				<FileValidationDialog
					open={openFileValidationDialog}
					setOpen={setOpenFileValidationDialog}
					parsedData={parsedFileData}
					setParsedData={setParsedFileData}
					originalFileContent={originalFileContent}
					file={file}
					token={token}
					ccid={ccid}
					currentRequest={currentOmrRequest}
					fetchRequestDetails={fetchRequestDetails}
					onUploadComplete={handleFileUploadComplete}
					setSnackbar={setSnackbar}
					uploadMode={selectedUploadMode}
				/>

				<VersionManagementDialog
					open={openVersionManagementDialog}
					setOpen={setOpenVersionManagementDialog}
					examProcessingState={examProcessingState}
					token={token}
					reqnum={currentOmrRequest?.reqnum}
					fetchRequestDetails={fetchRequestDetails}
					setSnackbar={setSnackbar}
				/>

				{/* Re-upload Confirmation Dialog */}
				<Dialog
					fullWidth
					maxWidth="md"
					open={openReuploadConfirmDialog}
					onClose={() => !reuploadDeleting && handleCancelReupload()}
					aria-labelledby="reupload-confirm-title"
					aria-describedby="reupload-confirm-description"
				>
					<DialogTitle id="reupload-confirm-title">Upload Decision Required</DialogTitle>
					<DialogContent>
						<DialogContentText component="div" id="reupload-confirm-description">
							Exam data already exists for Request #{currentOmrRequest?.reqnum}.
							{pendingReuploadData?.scenario === "replace_both" && (
								<>
									<br />
									The uploaded students match existing records (by sheet sequence + ID/name).
									Continue to <strong>replace keys and students</strong>.
								</>
							)}
							{pendingReuploadData?.scenario === "append_students" && (
								<>
									<br />
									Students differ, but all keys match existing keys. Continue to
									<strong>append students only</strong>.
								</>
							)}
							{pendingReuploadData?.scenario === "append_or_replace_both" && (
								<>
									<br />
									Students and keys differ. Choose whether to append both datasets, or replace both.
									{pendingReuploadData?.singleKeyAppendWillCreateNewVersion && (
										<>
											<br />
											Existing version code(s):{" "}
											<strong>{pendingReuploadData?.existingVersions?.join(", ")}</strong>. Choosing{" "}
											<strong>Append Keys + Students</strong> will create a new version code (
											<strong>
												{pendingReuploadData?.suggestedAppendVersion || "next available"}
											</strong>
											).
										</>
									)}
								</>
							)}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCancelReupload} disabled={reuploadDeleting}>
							Cancel
						</Button>
						{pendingReuploadData?.scenario === "replace_both" && (
							<Button
								onClick={() => handleProceedWithUploadMode("replace_both")}
								color="error"
								variant="contained"
								disabled={reuploadDeleting}
							>
								Replace Keys + Students
							</Button>
						)}
						{pendingReuploadData?.scenario === "append_students" && (
							<Button
								onClick={() => handleProceedWithUploadMode("append_students")}
								color="primary"
								variant="contained"
								disabled={reuploadDeleting}
							>
								Append Students
							</Button>
						)}
						{pendingReuploadData?.scenario === "append_or_replace_both" && (
							<>
								<Button
									onClick={() => handleProceedWithUploadMode("append_both")}
									color="primary"
									variant="contained"
									disabled={reuploadDeleting}
								>
									{pendingReuploadData?.singleKeyAppendWillCreateNewVersion
										? "Append as New Version"
										: "Append Keys + Students"}
								</Button>
								<Button
									onClick={() => handleProceedWithUploadMode("replace_both")}
									color="error"
									variant="contained"
									disabled={reuploadDeleting}
								>
									Replace Keys + Students
								</Button>
							</>
						)}
					</DialogActions>
				</Dialog>

				{/* Delete Exam Records Confirmation Dialog */}
				<Dialog
					fullWidth
					maxWidth="lg"
					open={openDeleteDialog}
					onClose={() => !loadingDelete && setOpenDeleteDialog(false)}
					aria-labelledby="delete-dialog-title"
					aria-describedby="delete-dialog-description"
				>
					<DialogTitle id="delete-dialog-title">Delete Exam Records</DialogTitle>
					<DialogContent>
						<DialogContentText component="div" id="delete-dialog-description">
							Select what to delete for Request #{currentOmrRequest?.reqnum}:
							<ul className="list-disc ml-6 mt-2">
								<li>
									<strong>Delete Keys</strong>: Deletes Answer Keys and Modification Logs.
								</li>
								<li>
									<strong>Delete Student Responses</strong>: Deletes Student Responses only.
								</li>
								<li>
									<strong>Delete Both</strong>: Deletes Answer Keys, Student Responses, and
									Modification Logs.
								</li>
							</ul>
							<strong className="block mt-2 text-red-600">This action cannot be undone.</strong>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setOpenDeleteDialog(false)} disabled={loadingDelete}>
							Cancel
						</Button>
						<Button
							onClick={() => handleDeleteExamRecords(DELETE_MODE.KEYS)}
							color="warning"
							variant="contained"
							disabled={loadingDelete}
							startIcon={
								loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
							}
						>
							{loadingDelete ? "Deleting..." : "Delete Keys"}
						</Button>
						<Button
							onClick={() => handleDeleteExamRecords(DELETE_MODE.STUDENTS)}
							color="primary"
							variant="contained"
							disabled={loadingDelete}
							startIcon={
								loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
							}
						>
							{loadingDelete ? "Deleting..." : "Delete Student Responses"}
						</Button>
						<Button
							onClick={() => handleDeleteExamRecords(DELETE_MODE.BOTH)}
							color="error"
							variant="contained"
							disabled={loadingDelete}
							startIcon={
								loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
							}
						>
							{loadingDelete ? "Deleting..." : "Delete Both"}
						</Button>
					</DialogActions>
				</Dialog>

				<div
					className={`flex-1 min-w-0 h-full min-h-[76dvh] relative pl-4 pr-4 md:pl-12 ${
						sidebarOpen ? "md:pr-12 " : "md:pr-20"
					}`}
					onClick={() => {
						if (sidebarOpen && !keepSidebarOpen) setSidebarOpen(false);
					}}
				>
					<ExamViewSnackbar
						open={snackbar.open}
						setOpen={(open) => setSnackbar((prev) => ({ ...prev, open }))}
						message={snackbar.message}
						snackbarAlertState={snackbar.severity}
					/>

					<div className="flex flex-row ">
						<div className="flex flex-col gap-6 w-full ">
							{currentOmrRequest && currentOmrRequest.reqnum && (
								<div
									key={currentOmrRequest.reqnum}
									className=" bg-digitalWhite text-black rounded-lg   px-2 pb-8 w-full"
								>
									<div className="flex flex-col w-full gap-4  ">
										<div className="w-full ">
											<h2
												className={`text-2xl font-semibold mb-2 text-darkGreen pt-2  w-full h-full `}
											>
												Request #
												<Tooltip
													placement="top"
													title={
														<span>
															{copyClicked ? (
																<CheckIcon className="text-primaryGold" />
															) : (
																<ContentCopyIcon />
															)}
														</span>
													}
													arrow
												>
													<button
														onClick={handleClickToCopyReqnum}
														className={`  rounded-md px-1 ${
															pulse
																? "animate-pulse text-secondaryOrange bg-gray-100"
																: "text-primary bg-gray-100"
														}`}
													>
														{currentOmrRequest.reqnum}
													</button>
												</Tooltip>
												<Chip
													label={
														currentOmrRequest.program === "1"
															? "GPScor"
															: currentOmrRequest.program === "2"
																? "MRScor"
																: "Assign2"
													}
													color={
														currentOmrRequest.program === "1"
															? "primary"
															: currentOmrRequest.program === "2"
																? "secondaryRed"
																: "secondaryPurple"
													}
													variant="contained"
													className="ml-4 font-medium"
													size="small"
												/>
												<Button
													variant="standard"
													size="small"
													onClick={handleRefreshCurrentRequest}
													disabled={
														refreshActionsDisabled ||
														fetchingRequestDetails ||
														!currentOmrRequest?.reqnum
													}
													startIcon={
														fetchingRequestDetails ? (
															<CircularProgress size={14} color="inherit" />
														) : (
															<RefreshIcon fontSize="small" />
														)
													}
													className="ml-3 align-middle hover:text-primary/80 text-primary"
												>
													{fetchingRequestDetails ? "Refreshing..." : "Refresh"}
												</Button>
											</h2>

											<div className="flex w-full z-10 mt-4 ">
												<div className="rounded-xl w-full border border-gray-200 bg-white/95 px-4 py-4 shadow-sm backdrop-blur ">
													<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between ">
														<div className={`flex flex-wrap justify-between  w-full`}>
															{workflowSections.map((section) => {
																const isActive = alignment === section.id;
																const isLocked = section.id !== "exam-info" && !examInDatabase;
																const isDownloadResults = section.id === "download-results";
																return (
																	<button
																		key={section.id}
																		type="button"
																		onClick={() => {
																			if (isDownloadResults && examInDatabase) {
																				setOpenDownloadDialog(true);
																			} else {
																				focusWorkflowSection(section.id);
																			}
																		}}
																		className={`flex min-w-[13rem] md:w-[24%] rounded-xl border px-3 py-3 text-left transition ${
																			isActive
																				? "border-primary bg-primary/5 shadow-sm"
																				: "border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/5"
																		}`}
																	>
																		<div className="flex flex-col items-center justify-center w-1/5">
																			{section.icon}
																		</div>
																		{/* <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-gray-400">
																			{section.step}
																		</p> */}
																		<div className="flex flex-col w-4/5 justify-between">
																			<p className=" text-sm font-semibold text-darkGreen">
																				{section.label}
																			</p>

																			<span
																				className={` w-full inline-flex  px-2 py-1 text-[0.7rem] font-semibold ${
																					isLocked
																						? "bg-gray-100 text-gray-500"
																						: "bg-primary/10 text-primary"
																				}`}
																			>
																				{section.status}
																			</span>
																		</div>
																	</button>
																);
															})}
														</div>
													</div>
												</div>
											</div>
										</div>

										<section ref={examInfoSectionRef} className="  flex flex-col gap-4">
											<div className="rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
												<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
													<div className="max-w-3xl">
														{/* <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-400">
															1
														</p> */}
														<h3 className="mt-1 text-xl font-semibold text-darkGreen">
															Exam Info + Upload
														</h3>
													</div>
													<Chip
														label={
															examInDatabase
																? "Exam Data Loaded"
																: examDataPending
																	? "Processing Upload"
																	: "Upload Required"
														}
														color={
															examInDatabase ? "success" : examDataPending ? "warning" : "default"
														}
														variant={examInDatabase ? "filled" : "outlined"}
													/>
												</div>

												<div className="flex flex-col gap-2 mt-8">
													<Divider />
													<div className="grid md:grid-cols-2  gap-4 ">
														<div className="flex flex-col gap-2 min-w-[300px]  ">
															<div className="flex justify-between">
																<p className="font-semibold">Instructor:</p>
																<p>{currentOmrRequest.instructor_name}</p>
															</div>
															<Divider />
															<div className="flex justify-between hover:cursor-pointer ">
																<p className="font-semibold">Email:</p>

																<Tooltip
																	placement="top"
																	title={
																		<span>
																			{copyClicked ? (
																				<CheckIcon className="text-primaryGold" />
																			) : (
																				<ContentCopyIcon />
																			)}
																		</span>
																	}
																	arrow
																>
																	<button
																		onClick={handleClickToCopyEmail}
																		className={`  rounded-md px-1 text-primary`}
																	>
																		{currentOmrRequest.email1}
																	</button>
																</Tooltip>
															</div>

															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">Delivery:</p>
																<p>{currentOmrRequest.delivery}</p>
															</div>
															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">Course Title:</p>
																<p>{currentOmrRequest.coursetitle}</p>
															</div>
															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">Processing:</p>
																<div className="flex flex-col items-end">
																	<p
																		className={`${
																			examProcessingState ? "text-primary" : "text-secondaryRed"
																		} font-semibold`}
																	>
																		{examProcessingState
																			? "File Loaded"
																			: examDataPending
																				? "Processing Upload"
																				: "Waiting for Exam File"}
																	</p>
																	{examInDatabase && (
																		<p className="text-xs text-gray-500">
																			{responses.length} response
																			{responses.length === 1 ? "" : "s"} and{" "}
																			{answerKeyVersions.length} key version
																			{answerKeyVersions.length === 1 ? "" : "s"}
																		</p>
																	)}
																</div>
															</div>
														</div>
														{/* <Divider className="md:hidden" /> */}
														<div className="px-2 flex flex-col gap-2 min-w-[300px] ">
															<div className="flex justify-between">
																<p className="font-semibold">LQNUM:</p>
																<p>{currentOmrRequest.lqnum}</p>
															</div>
															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">HQNUM:</p>
																<p>{currentOmrRequest.hqnum}</p>
															</div>
															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">QLEN:</p>
																<p>{currentOmrRequest.qlen}</p>
															</div>
															<Divider />
															<div className="flex justify-between">
																<p className="font-semibold">NKEYS:</p>
																<p>{currentOmrRequest.nkeys}</p>
															</div>
															<Divider />
															<div className="flex flex-col gap-1">
																<div className="flex justify-between items-center">
																	<p className="font-semibold">Class IDs:</p>
																	{modifyingClassID ? null : (
																		<Tooltip
																			placement="top"
																			title={<span>Click to modify Class IDs</span>}
																			arrow
																		>
																			<span
																				onClick={handleClickToModifyClassID}
																				className={`hover:cursor-pointer hover:underline truncate max-w-[200px] ${
																					classListError.error
																						? "text-secondaryRed"
																						: "text-primary"
																				} font-semibold rounded-md group`}
																			>
																				{classId || "-"}
																			</span>
																		</Tooltip>
																	)}
																</div>
																{classListError.error && (
																	<span className="text-secondaryRed text-xs font-medium">
																		{classListError.message}
																	</span>
																)}
																{modifyingClassID &&
																	(loadingUpdateClassID ? (
																		<CircularProgress size={24} className="text-primaryGold" />
																	) : (
																		<div className="flex flex-col gap-2">
																			<input
																				value={classIDInputValue}
																				type="text"
																				onChange={handleChangeModifyClassIDInputValue}
																				className={`w-full text-primary font-semibold rounded-md ring-2 ring-primary px-2 py-1`}
																				placeholder="Enter class IDs, e.g. 12345,67890"
																			/>
																			<div className="flex items-center gap-2">
																				<Button
																					variant="contained"
																					onClick={handleUpdateReqClassID}
																					disabled={!!classIdError || !classIDInputValue}
																					className={`hover:cursor-pointer text-primary font-semibold rounded-md group text-center`}
																				>
																					Save
																				</Button>
																				<button onClick={handleClickToModifyClassID}>
																					<svg
																						className="h-6 w-6 text-gray-500 hover:text-gray-700"
																						xmlns="http://www.w3.org/2000/svg"
																						fill="none"
																						viewBox="0 0 24 24"
																						stroke="currentColor"
																						aria-hidden="true"
																					>
																						<path
																							strokeLinecap="round"
																							strokeLinejoin="round"
																							strokeWidth={2}
																							d="M6 18L18 6M6 6l12 12"
																						/>
																					</svg>
																				</button>
																			</div>
																			{classIdError && (
																				<p className="text-secondaryRed text-xs font-medium">
																					{classIdError}
																				</p>
																			)}
																		</div>
																	))}
															</div>
															<Divider className="md:hidden" />
														</div>
														<div className=" col-span-2 grid grid-cols-2 w-full gap-4 md:pb-0 pb-4">
															<div>
																<p className="font-semibold">Notes:</p>
																<p>{currentOmrRequest.notes}</p>
															</div>
															<div className="col-span-1 flex w-full gap-4 px-2 justify-between">
																{isAdmin && examInDatabase && (
																	<Button
																		variant="outlined"
																		color="error"
																		fullWidth
																		startIcon={<DeleteIcon />}
																		onClick={() => setOpenDeleteDialog(true)}
																	>
																		Delete Exam Records
																	</Button>
																)}
																<Button
																	variant="outlined"
																	fullWidth
																	onClick={() => {
																		handlePrint(currentOmrRequest.reqnum);
																	}}
																>
																	Download Request Receipt
																</Button>
															</div>
														</div>
													</div>

													<Divider />

													<div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-4 mt-8">
														<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
															<div className="max-w-3xl">
																<p className="text-sm font-semibold text-darkGreen">
																	Upload raw scanner data
																</p>
																<p className="mt-1 text-sm text-gray-600">
																	Can upload: A single class in one file, multiple classes in one
																	file, or multiple files for multiple classes. If multiple files
																	are being uploaded sequentially, you will be prompted to choose
																	whether to append or replace existing data.
																</p>
															</div>
															{examInDatabase && (
																<Button
																	variant="outlined"
																	onClick={() => setOpenVersionManagementDialog(true)}
																>
																	Edit Uploaded Versions
																</Button>
															)}
														</div>

														{isAdmin ? (
															<div className="mt-4 flex md:flex-row flex-col gap-2 items-center">
																<DragAndDropFile
																	isAdmin={isAdmin}
																	file={file}
																	examFileUploading={examFileUploading}
																	examInDatabase={examInDatabase}
																	fetchingRequestDetails={fetchingRequestDetails}
																	handleUploadFile={handleUploadFile}
																	accept="*"
																	multiple={inputSettings.multipleFiles}
																/>
															</div>
														) : (
															<p className="mt-4 text-sm text-gray-600">
																Only administrators can upload or replace scanner files for this
																request.
															</p>
														)}
													</div>
												</div>
											</div>
										</section>

										<section ref={keysAndStatsSectionRef} className="  flex flex-col gap-4">
											<div className="rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
												<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
													<div className="max-w-3xl">
														<h3 className="mt-1 text-xl font-semibold text-darkGreen">
															Answer Keys + Statistics
														</h3>
													</div>
													<Chip
														label={
															examInDatabase
																? editMode
																	? "Editing Keys"
																	: "Ready"
																: examDataPending
																	? "Waiting for Upload"
																	: "Locked"
														}
														color={examInDatabase ? (editMode ? "warning" : "primary") : "default"}
														variant={examInDatabase ? "filled" : "outlined"}
													/>
												</div>
												<div className="py-4">
													{examInDatabase ? (
														<KeysAndStatistics
															fetchRequestDetails={fetchRequestDetails}
															token={token}
															reqnum={currentOmrRequest?.reqnum}
															setSnackbar={setSnackbar}
															editMode={editMode}
															setEditMode={setEditMode}
															onOpenVersionManager={() => setOpenVersionManagementDialog(true)}
															canManageVersions={isAdmin}
														/>
													) : (
														renderLockedWorkflowSection(
															"Answer keys and statistics will appear here",
															"Upload the raw scanner file in Step 1 to populate the detected key versions, item analyses, and editable answer keys for this request.",
														)
													)}
												</div>
											</div>
										</section>

										<section ref={classAndScoresSectionRef} className="  flex flex-col gap-4">
											<div className="rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
												<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
													<div className="max-w-3xl">
														<h3 className="mt-1 text-xl font-semibold text-darkGreen">
															Matching + Results
														</h3>

														{examInDatabase && (
															<p className="mt-2 text-xs text-gray-500">
																{responses.length} scanned response
																{responses.length === 1 ? "" : "s"} loaded.
																{classListError.error
																	? ` ${classListError.message}`
																	: classListFile?.classlist?.length > 0
																		? ` ${classListFile.classlist.length} class list record${
																				classListFile.classlist.length === 1 ? "" : "s"
																			} ready for matching.`
																		: classId
																			? " Class list will be fetched automatically for matching."
																			: " Add class IDs in Step 1 if matching is required."}
															</p>
														)}
													</div>

													<Chip
														label={matchingProgressLabel}
														color={
															examInDatabase &&
															(gradeWithoutMatching ||
																(allResponsesAttempted && unmatchedStudents.length === 0))
																? "success"
																: examInDatabase
																	? "primary"
																	: "default"
														}
														variant={examInDatabase ? "filled" : "outlined"}
													/>
												</div>
												<div className="mt-4">
													{examInDatabase ? (
														<ClassAndScores
															rescoreLoading={rescoreLoading}
															setRescoreLoading={setRescoreLoading}
															auth={auth}
															setSnackbar={setSnackbar}
															handleDownloadCsv={handleDownloadCsv}
															handleRescoreExam={handleRescoreExam}
															editMode={editMode}
															token={token}
															reqnum={currentOmrRequest?.reqnum}
															fetchRequestDetails={fetchRequestDetails}
															getCsvBlobs={getCsvBlobs}
															openDownloadDialog={openDownloadDialog}
															setOpenDownloadDialog={setOpenDownloadDialog}
															classListError={classListError}
														/>
													) : (
														renderLockedWorkflowSection(
															"Student matching and downloads stay in this section",
															"Once the scanner file has been uploaded, this area will show the student response grid, matching dialogs, rescoring actions, and result downloads.",
														)
													)}
												</div>
											</div>
										</section>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				{sidebarMode === "dialog" && sidebarOpen && (
					<div
						className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
						onClick={() => setSidebarOpen(false)}
					/>
				)}
				<div
					className={`${
						sidebarMode === "docked"
							? "sticky top-0 right-0 z-20 h-full shrink-0 self-start"
							: sidebarMode === "dialog" && !sidebarOpen
								? //centered vertically
									"fixed right-0 z-50 top-1/3  "
								: "fixed top-4 bottom-4 right-0 z-50 min-w-[60dvw]"
					} transition-[width,transform] duration-300 ease-out`}
					style={{
						width: `${effectiveSidebarWidth}px`,
						flexBasis: sidebarMode === "docked" ? `${effectiveSidebarWidth}px` : undefined,
						minWidth: sidebarMode === "docked" ? `${effectiveSidebarWidth}px` : undefined,
						transform:
							sidebarMode === "docked"
								? "translateX(0)"
								: `translateX(${sidebarOpen ? sidebarOffsetX : 0}px)`,
					}}
				>
					<ExamViewSideBar
						token={token}
						setFile={setFile}
						setAlignment={focusWorkflowSection}
						setSnackbar={setSnackbar}
						editMode={editMode}
						access={access}
						ccid={ccid}
						sidebarOpen={sidebarOpen}
						setSidebarOpen={setSidebarOpen}
						refreshDisabled={refreshActionsDisabled}
						keepSidebarOpen={keepSidebarOpen}
						setKeepSidebarOpen={setKeepSidebarOpen}
						sidebarMode={sidebarMode}
						setSidebarMode={updateSidebarMode}
						sidebarWidth={sidebarWidth}
						onResizeStart={handleSidebarResizeStart}
						onDragStart={handleSidebarDragStart}
						onResetFloatingPosition={handleResetSidebarPosition}
						isFloatingSidebar={isFloatingSidebar}
						shellClassName={sidebarMode === "dialog" ? "rounded-l-xl rounded-r-none" : ""}
						shellStyle={
							sidebarMode === "dialog"
								? sidebarOpen
									? { height: "calc(100dvh - 2rem)" }
									: { height: "10rem" }
								: sidebarMode === "slide"
									? { height: "calc(80dvh - 2rem)", top: "20dvh" }
									: {}
						}
					/>
				</div>
			</div>
		</div>
	);
}
